import { db, storage } from '../config/firebase';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore'; // Added updateDoc, deleteDoc
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'; // Added deleteObject

const BENNY_JOURNAL_ENTRIES_COLLECTION = 'bennyJournalEntries';
const BENNY_JOURNAL_FILES_STORAGE_PATH = 'bennyJournalFiles';

export const ALLOWED_JOURNAL_FILE_TYPES_EXT = ['.pdf'];
export const ALLOWED_JOURNAL_FILE_TYPES_MIME = ['application/pdf'];
export const ALLOWED_AUDIO_FILE_TYPES_EXT = ['.mp3', '.m4a', '.wav'];
export const ALLOWED_AUDIO_FILE_TYPES_MIME = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-m4a'];
export const ACCEPT_FILE_TYPES_STRING = ALLOWED_JOURNAL_FILE_TYPES_EXT.join(',');
export const ACCEPT_AUDIO_TYPES_STRING = ALLOWED_AUDIO_FILE_TYPES_EXT.join(',');


/**
 * Uploads a journal file and creates an entry in Firestore.
 * Note: PDF text extraction is handled automatically by a Cloud Function (extractPdfText)
 * that triggers when a new bennyJournalEntry document is created with a PDF file.
 * The extracted text is stored in the 'pdfTextContent' field for search functionality.
 * 
 * @param {File} file - The file to upload.
 * @param {string} title - The title of the journal entry.
 * @param {string} description - The description of the journal entry.
 * @param {string} userId - The ID of the user uploading the file.
 * @param {string} userName - The display name of the user uploading the file.
 * @param {string} [microsoftLink] - Optional Microsoft link for the journal entry.
 * @returns {Promise<string>} The ID of the newly created journal entry.
 */
export const uploadJournalEntry = async (file, title, description, userId, userName, microsoftLink) => {
  console.error('[uploadJournalEntry] Called with:', { file, title, description, userId, userName, microsoftLink });

  if (!userId || !userName) {
    console.error('[uploadJournalEntry] Error: User information is required.');
    throw new Error('User information is required.');
  }
  if (!file && !microsoftLink) {
    console.error('[uploadJournalEntry] Error: Either a file or a Microsoft link is required.');
    throw new Error('Either a file or a Microsoft link is required.');
  }

  const entryData = {
    userId,
    userName,
    title,
    description,
    createdAt: serverTimestamp(),
  };

  if (microsoftLink) {
    console.error('[uploadJournalEntry] Microsoft link provided, adding to entryData.');
    entryData.microsoftLink = microsoftLink;
    // If only a link is provided initially, mark fileType. This might be overwritten if a file is also present.
    if (!file) {
        entryData.fileType = 'link';
    }
  }

  if (file) {
    console.error('[uploadJournalEntry] File provided, proceeding with file upload logic.');
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_JOURNAL_FILE_TYPES_EXT.includes(fileExtension)) {
      console.error(`[uploadJournalEntry] Error: Invalid file type: ${fileExtension}`);
      throw new Error(`Invalid file type. Allowed types: ${ALLOWED_JOURNAL_FILE_TYPES_EXT.join(', ')}`);
    }

    const timestamp = Date.now();
    const storageFilePath = `${BENNY_JOURNAL_FILES_STORAGE_PATH}/${userId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, storageFilePath);
    
    console.error('[uploadJournalEntry] Attempting to upload file to:', storageFilePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Optional: handle progress
          // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // console.log('Upload is ' + progress + '% done');
        },
        (error) => {
          console.error('[uploadJournalEntry] Firebase Storage upload failed:', error);
          reject(error);
        },
        async () => {
          try {
            console.error('[uploadJournalEntry] File upload successful. Getting download URL.');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.error('[uploadJournalEntry] Download URL obtained:', downloadURL);
            entryData.fileName = file.name;
            entryData.fileType = fileExtension.replace('.', ''); // Store extension without dot
            entryData.storagePath = storageFilePath;
            entryData.downloadURL = downloadURL;
            
            console.error('[uploadJournalEntry] Attempting to add document to Firestore with file data:', entryData);
            const docRef = await addDoc(collection(db, BENNY_JOURNAL_ENTRIES_COLLECTION), entryData);
            console.error('[uploadJournalEntry] Firestore document added successfully with file. ID:', docRef.id);
            resolve(docRef.id);
          } catch (dbError) {
            console.error('[uploadJournalEntry] Error creating journal entry in Firestore after file upload:', dbError);
            reject(dbError);
          }
        }
      );
    });
  } else {
    // This block executes if there is no file, meaning only a microsoftLink was provided (or neither, but that's caught earlier)
    console.error('[uploadJournalEntry] No file provided, attempting to add document to Firestore with link data only:', entryData);
    try {
      const docRef = await addDoc(collection(db, BENNY_JOURNAL_ENTRIES_COLLECTION), entryData);
      console.error('[uploadJournalEntry] Firestore document added successfully with link only. ID:', docRef.id);
      return docRef.id; // Resolve with the new document ID
    } catch (dbError) {
      console.error('[uploadJournalEntry] Error creating journal entry in Firestore with Microsoft link only:', dbError);
      throw dbError; // Re-throw to be caught by the caller
    }
  }
};

/**
 * Uploads an audio file for a journal entry and updates the entry with the audio URL.
 * @param {string} entryId - The ID of the journal entry.
 * @param {File} audioFile - The audio file to upload.
 * @returns {Promise<string>} The download URL of the uploaded audio file.
 */
export const uploadAudioForEntry = async (entryId, audioFile) => {
  if (!audioFile) {
    throw new Error('Audio file is required.');
  }
  if (!entryId) {
    throw new Error('Entry ID is required.');
  }

  const fileExtension = audioFile.name.split('.').pop();
  if (!ALLOWED_AUDIO_FILE_TYPES_EXT.includes(`.${fileExtension.toLowerCase()}`)) {
    throw new Error(`Invalid audio file type. Allowed types: ${ALLOWED_AUDIO_FILE_TYPES_EXT.join(', ')}`);
  }

  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, `${BENNY_JOURNAL_FILES_STORAGE_PATH}/${entryId}_audio_${Date.now()}.${fileExtension}`);
    const uploadTask = uploadBytesResumable(storageRef, audioFile);

    uploadTask.on(
      'state_changed',
      null,
      (error) => {
        console.error('Error uploading audio file:', error);
        reject(error);
      },
      async () => {
        try {
          const audioURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Update the journal entry with audio URL
          const entryRef = doc(db, BENNY_JOURNAL_ENTRIES_COLLECTION, entryId);
          await updateDoc(entryRef, {
            audioURL,
            audioFileName: audioFile.name
          });
          
          resolve(audioURL);
        } catch (error) {
          console.error('Error updating entry with audio URL:', error);
          reject(error);
        }
      }
    );
  });
};

/**
 * Adds a comment to a specific Benny's Journal entry.
 * @param {string} journalEntryId - The ID of the journal entry to comment on.
 * @param {object} commentData - Data for the new comment.
 * @param {string} commentData.userId - The UID of the user making the comment.
 * @param {string} commentData.userName - The display name of the user.
 * @param {'inCharacter' | 'outOfCharacter'} commentData.commentType - The type of comment.
 * @param {string} commentData.text - The content of the comment.
 * @param {string} [commentData.characterId] - Optional: The ID of the character if commentType is 'inCharacter'.
 * @param {string} [commentData.characterName] - Optional: The name of the character if commentType is 'inCharacter'.
 * @param {string} [commentData.characterImageUrl] - Optional: The image URL of the character if commentType is 'inCharacter'.
 * @returns {Promise<string>} The ID of the newly created comment document.
 */
export const addCommentToJournalEntry = async (journalEntryId, commentData) => {
  if (!journalEntryId) {
    throw new Error('Journal Entry ID is required to add a comment.');
  }
  if (!commentData || !commentData.userId || !commentData.text || !commentData.commentType) {
    throw new Error('User ID, text, and comment type are required for a comment.');
  }

  const newComment = {
    ...commentData,
    createdAt: serverTimestamp(),
  };

  try {
    const commentsCollectionRef = collection(db, BENNY_JOURNAL_ENTRIES_COLLECTION, journalEntryId, 'comments');
    const docRef = await addDoc(commentsCollectionRef, newComment);
    return docRef.id;
  } catch (error) {
    console.error('Error adding comment to journal entry:', error);
    throw new Error('Failed to add comment.');
  }
};

/**
 * Fetches all comments for a specific Benny's Journal entry, ordered by creation date (oldest first).
 * @param {string} journalEntryId - The ID of the journal entry.
 * @returns {Promise<Array<object>>} An array of comment objects.
 */
export const getCommentsForJournalEntry = async (journalEntryId) => {
  if (!journalEntryId) {
    console.warn('Journal Entry ID is required to fetch comments.');
    return []; // Return empty array or throw error as preferred
  }
  try {
    const commentsCollectionRef = collection(db, BENNY_JOURNAL_ENTRIES_COLLECTION, journalEntryId, 'comments');
    const q = query(commentsCollectionRef, orderBy('createdAt', 'asc')); // Order by 'asc' for chronological order
    const querySnapshot = await getDocs(q);
    const comments = [];
    querySnapshot.forEach((doc) => {
      comments.push({ id: doc.id, ...doc.data() });
    });
    return comments;
  } catch (error) {
    console.error('Error fetching comments for journal entry:', error);
    throw error;
  }
};

/**
 * Fetches all Benny's Journal entries from Firestore, ordered by creation date.
 * @returns {Promise<Array<object>>} An array of journal entry objects.
 */
export const getAllJournalEntries = async () => {
  try {
    const q = query(collection(db, BENNY_JOURNAL_ENTRIES_COLLECTION), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const entries = [];
    querySnapshot.forEach((doc) => {
      entries.push({ id: doc.id, ...doc.data() });
    });
    return entries;
  } catch (error) {
    console.error("Error fetching journal entries:", error);
    throw error;
  }
};

/**
 * Fetches a single Benny's Journal entry by its ID.
 * @param {string} journalEntryId - The ID of the journal entry to fetch.
 * @returns {Promise<object|null>} The journal entry object if found, otherwise null.
 */
export const getJournalEntryById = async (journalEntryId) => {
  if (!journalEntryId) {
    console.error("Journal Entry ID is required to fetch an entry.");
    return null;
  }
  try {
    const docRef = doc(db, BENNY_JOURNAL_ENTRIES_COLLECTION, journalEntryId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("No such journal entry!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching journal entry by ID:", error);
    throw error;
  }
};

/**
 * Updates an existing Benny\'s Journal entry in Firestore.
 * Only title, description, and microsoftLink can be updated.
 * @param {string} journalEntryId - The ID of the journal entry to update.
 * @param {object} updatedData - Object containing the fields to update.
 * @param {string} [updatedData.title] - The new title.
 * @param {string} [updatedData.description] - The new description.
 * @param {string} [updatedData.microsoftLink] - The new Microsoft link.
 * @param {string} userId - The UID of the user performing the update.
 * @returns {Promise<void>}
 */
export const updateJournalEntry = async (journalEntryId, updatedData, userId) => {
  if (!journalEntryId || !updatedData || !userId) {
    console.error("Journal Entry ID, updated data, and user ID are required for update.");
    throw new Error("Missing required parameters for updating journal entry.");
  }

  const entryRef = doc(db, BENNY_JOURNAL_ENTRIES_COLLECTION, journalEntryId);
  const entryDoc = await getDoc(entryRef);

  if (!entryDoc.exists()) {
    throw new Error("Journal entry not found.");
  }

  // For now, only allow admin or original author to update.
  // You might want to refine this logic based on your app's roles.
  const entryData = entryDoc.data();
  // Assuming firestoreUser object has a roles field. This check might need to be adapted
  // if user role information is fetched differently or if you have a global isAdmin check.
  // For simplicity, this example assumes userId is sufficient for an author check.
  // A more robust check would involve fetching the current user's roles.
  // const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;
  // if (!isAdmin && entryData.userId !== userId) {
  //   throw new Error("User not authorized to update this journal entry.");
  // }
  // Simplified check: only author can edit. Add admin check if needed.
  if (entryData.userId !== userId) {
      throw new Error("User not authorized to update this journal entry.");
  }


  const dataToUpdate = {};
  if (updatedData.title !== undefined) {
    dataToUpdate.title = updatedData.title;
  }
  if (updatedData.description !== undefined) {
    dataToUpdate.description = updatedData.description;
  }
  if (updatedData.microsoftLink !== undefined) { // Allow clearing the link
    dataToUpdate.microsoftLink = updatedData.microsoftLink;
  }
  
  if (Object.keys(dataToUpdate).length === 0) {
    console.log("No valid fields to update.");
    return; // Or throw an error if an update was expected
  }

  dataToUpdate.updatedAt = serverTimestamp();
  dataToUpdate.updatedBy = userId;

  try {
    await updateDoc(entryRef, dataToUpdate);
    console.log("Journal entry updated successfully:", journalEntryId);
  } catch (error) {
    console.error("Error updating journal entry:", error);
    throw error;
  }
};

/**
 * Deletes a Benny\'s Journal entry from Firestore and its associated file from Storage (if any).
 * @param {string} journalEntryId - The ID of the journal entry to delete.
 * @param {string} userId - The UID of the user performing the deletion (for authorization).
 * @returns {Promise<void>}
 */
export const deleteJournalEntry = async (journalEntryId, userId) => {
  if (!journalEntryId || !userId) {
    console.error("Journal Entry ID and User ID are required for deletion.");
    throw new Error("Missing required parameters for deleting journal entry.");
  }

  const entryRef = doc(db, BENNY_JOURNAL_ENTRIES_COLLECTION, journalEntryId);
  const entryDoc = await getDoc(entryRef);

  if (!entryDoc.exists()) {
    throw new Error("Journal entry not found.");
  }

  const entryData = entryDoc.data();
  // For now, only allow admin or original author to delete.
  // Similar to update, a more robust role check might be needed.
  // const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;
  // if (!isAdmin && entryData.userId !== userId) {
  //  throw new Error("User not authorized to delete this journal entry.");
  // }
  // Simplified check: only author can delete. Add admin check if needed.
  if (entryData.userId !== userId) {
      throw new Error("User not authorized to delete this journal entry.");
  }

  try {
    // Delete the file from Firebase Storage if it exists
    if (entryData.storagePath) {
      const fileRef = ref(storage, entryData.storagePath);
      try {
        await deleteObject(fileRef);
        console.log("Journal file deleted from Storage:", entryData.storagePath);
      } catch (storageError) {
        // Log error but proceed to delete Firestore doc, as the file might already be deleted
        // or there might be permission issues that shouldn't block DB cleanup.
        console.error("Error deleting journal file from Storage:", storageError);
        // Optionally, rethrow if file deletion is critical and must succeed: throw storageError;
      }
    }

    // Delete the Firestore document
    await deleteDoc(entryRef);
    console.log("Journal entry deleted successfully from Firestore:", journalEntryId);

    // Note: Deleting comments subcollection is not handled here.
    // If comments need to be deleted, you'd need to list and delete them,
    // or use a Firebase Cloud Function for recursive deletion.
  } catch (error) {
    console.error("Error deleting journal entry:", error);
    throw error;
  }
};
